import {
    QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { AxiosInstance } from 'axios';

import { toCamelCase, toSnakeCase } from '@/lib/case';

import { useAuthAxios } from '@/hooks/useAuthAxios';

import {
    BulkFileDeletionResult,
    CorrectionView,
    DeleteFileResponse,
    DocumentSchema,
    IngestionStatusFilter,
    PaginatedDocumentsSchema,
    PaginatedFilesTree,
    SaveCorrectionsResponse,
} from './types';
import {
    removeDocumentFromTree,
    removeFilesFromTree,
    setFileStatusInTree,
    treeHasProcessingNode,
} from './utils';

const FILES_WITH_DOCS_KEY = 'filesWithDocuments';
const FILES_TREE_KEY = 'filesTree';

// Refresh every file/document listing after a mutation that changes them. Pass
// refetchType 'none' to only mark them stale (refetch on next mount) without
// refetching now.
export const invalidateFileQueries = (
    queryClient: QueryClient,
    refetchType?: 'none',
) =>
    Promise.all([
        queryClient.invalidateQueries({
            queryKey: [FILES_WITH_DOCS_KEY],
            refetchType,
        }),
        queryClient.invalidateQueries({
            queryKey: [FILES_TREE_KEY],
            refetchType,
        }),
        queryClient.invalidateQueries({ queryKey: ['documents'], refetchType }),
    ]);

// Duplicate checks rerun detection under a project lock, so only deletions
// should refresh them here; folder moves only need listing invalidation.
// Folder/bulk deletes fire one cascade mutation per file, so the refresh is
// debounced to a single rescan once the last one lands instead of one per file.
export const DUPLICATE_REFRESH_DEBOUNCE_MS = 300;
let duplicateRefreshTimer: ReturnType<typeof setTimeout> | undefined;

const invalidateDuplicateQueries = (queryClient: QueryClient) => {
    clearTimeout(duplicateRefreshTimer);
    duplicateRefreshTimer = setTimeout(() => {
        duplicateRefreshTimer = undefined;
        void queryClient.invalidateQueries({ queryKey: ['duplicateCheck'] });
    }, DUPLICATE_REFRESH_DEBOUNCE_MS);
};

// Optimistically drop deleted files from the cached tree in one pass. Does not
// refetch; the caller issues one trailing invalidateFileQueries to reconcile.
export const removeFilesFromCache = (
    queryClient: QueryClient,
    fileIds: number[],
) => {
    const ids = new Set(fileIds);
    queryClient.setQueriesData<PaginatedFilesTree>(
        { queryKey: [FILES_TREE_KEY] },
        (old) => {
            if (!old) return old;
            const results = removeFilesFromTree(old.results, ids);
            if (results === old.results) return old;
            const total = old.total - (old.results.length - results.length);
            return { ...old, results, total };
        },
    );
};

// Optimistically drop a deleted document from the cached tree, decrementing its
// file's documentCount. Does not refetch.
const removeDocumentFromCache = (
    queryClient: QueryClient,
    docId: number,
) => {
    queryClient.setQueriesData<PaginatedFilesTree>(
        { queryKey: [FILES_TREE_KEY] },
        (old) => {
            if (!old) return old;
            const results = removeDocumentFromTree(old.results, docId);
            if (results === old.results) return old;
            return { ...old, results };
        },
    );
};

// Optimistically flip files to processing in the cached tree so a reingest click
// gives instant feedback. Does not refetch; the caller reconciles with a trailing
// invalidateFileQueries.
export const markFilesProcessingInCache = (
    queryClient: QueryClient,
    fileIds: number[],
) => {
    const ids = new Set(fileIds);
    queryClient.setQueriesData<PaginatedFilesTree>(
        { queryKey: [FILES_TREE_KEY] },
        (old) => {
            if (!old) return old;
            const results = setFileStatusInTree(old.results, ids, 'processing');
            if (results === old.results) return old;
            return { ...old, results };
        },
    );
};

export const reingestFile = async ({
    client,
    fileId,
}: {
    client: AxiosInstance;
    fileId: number;
}) => {
    await client.post(`/api/files/${fileId}/reingest`);
};

export const isReingestConflictError = (err: unknown): boolean =>
    (err as { response?: { status?: number } } | undefined)?.response
        ?.status === 409;

export const useGetDocuments = ({
    projectId,
    limit,
    skip,
    title,
    type,
    isEnabled = true,
    refetchInterval,
}: {
    projectId?: number;
    limit?: number;
    skip?: number;
    title?: string | null;
    type?: string;
    isEnabled?: boolean;
    // Poll while a caller is waiting on ingestion (e.g. the B004 update flow); default off.
    refetchInterval?: number | false;
}) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['documents', { projectId, limit, skip, title, type }],
        queryFn: async () => {
            const response = await client.get(`/api/leasing/documents`, {
                params: { project_id: projectId, limit, skip, title, type },
            });

            return toCamelCase<PaginatedDocumentsSchema>(response.data);
        },
        enabled: enabled && isEnabled,
        refetchInterval,
    });
};

export const useGetDocument = (docId: number | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['leasingDocument', docId],
        queryFn: async () => {
            const response = await client.get(`/api/leasing/${docId}`);
            return toCamelCase<DocumentSchema>(response.data);
        },
        enabled: enabled && docId !== null,
    });
};

export const useFilesTree = ({
    limit,
    skip,
    search,
    docType,
    projectId,
    ingestionStatus,
    isEnabled = true,
    // Callers that only need a static snapshot (e.g. duplicate review, which
    // re-displays a presigned URL that rotates every poll) can pass `false`
    // to opt out of the ingestion-status polling below.
    refetchInterval,
}: {
    limit?: number;
    skip?: number;
    search?: string | null;
    docType?: string | null;
    projectId?: number | null;
    ingestionStatus?: IngestionStatusFilter | null;
    isEnabled?: boolean;
    refetchInterval?: number | false;
}) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: [
            FILES_TREE_KEY,
            { limit, skip, search, docType, projectId, ingestionStatus },
        ],
        queryFn: async () => {
            const response = await client.get(`/api/files/tree`, {
                params: {
                    limit,
                    skip,
                    search: search || undefined,
                    doc_type: docType || undefined,
                    project_id: projectId || undefined,
                    ingestion_status:
                        ingestionStatus && ingestionStatus !== 'all'
                            ? ingestionStatus
                            : undefined,
                },
            });

            return toCamelCase<PaginatedFilesTree>(response.data);
        },
        enabled: enabled && isEnabled,
        refetchInterval:
            refetchInterval !== undefined
                ? refetchInterval
                : (query) =>
                      treeHasProcessingNode(query.state.data?.results ?? [])
                          ? 5_000
                          : 30_000,
    });
};

export const useDeleteFileCascade = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (fileId: number) => {
            const response = await client.delete(
                `/api/files/${fileId}/with_documents`,
            );
            return toCamelCase<DeleteFileResponse>(response.data);
        },
        // Mark the listings stale (no refetch) so other callers stay correct.
        // The Doc Repo handlers optimistically prune + do their own refetch.
        onSuccess: () => {
            invalidateFileQueries(queryClient, 'none');
            invalidateDuplicateQueries(queryClient);
        },
    });
};

// Mirrors the filter params useFilesTree sends.
export interface TreeFilters {
    search?: string | null;
    docType?: string | null;
    projectId?: number | null;
    ingestionStatus?: IngestionStatusFilter | null;
}

export type BulkDeleteFilesArgs =
    | { fileIds: number[] }
    | { selectAll: true; filters: TreeFilters };

export const useBulkDeleteFiles = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: BulkDeleteFilesArgs) => {
            const payload =
                'selectAll' in args
                    ? { selectAll: true, filters: args.filters }
                    : { fileIds: args.fileIds };
            const response = await client.post(
                `/api/files/bulk-delete`,
                toSnakeCase(payload),
            );
            return toCamelCase<BulkFileDeletionResult>(response.data);
        },
        onSuccess: (result) => {
            if (result.deletedCount > 0 || result.alreadyDeletedCount > 0) {
                invalidateDuplicateQueries(queryClient);
            }
        },
        // Caller reconciles listing cache + selection from the result.
    });
};

export const useDeleteDocument = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number) => {
            await client.delete(`/api/leasing/documents/${id}`);
        },
        onSuccess: (_data, id) => {
            removeDocumentFromCache(queryClient, id);
            invalidateDuplicateQueries(queryClient);
            // Mark the documents query stale without refetching it here.
            queryClient.invalidateQueries({
                queryKey: ['documents'],
                refetchType: 'none',
            });
        },
    });
};

const CORRECTIONS_KEY = 'corrections';

export const useCorrectionFields = (docId: number | null, isEnabled = true) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: [CORRECTIONS_KEY, docId],
        queryFn: async () => {
            const response = await client.get(
                `/api/leasing/documents/${docId}/extractions`,
            );
            return toCamelCase<CorrectionView>(response.data);
        },
        enabled: enabled && isEnabled && docId !== null,
    });
};

export const useSaveCorrections = (docId: number) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            overrides: {
                modelClass: string;
                fields: Record<string, unknown>;
            }[],
        ) => {
            const response = await client.post(
                `/api/leasing/documents/${docId}/extractions`,
                toSnakeCase({ overrides }),
            );
            return toCamelCase<SaveCorrectionsResponse>(response.data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [FILES_WITH_DOCS_KEY] });
            queryClient.invalidateQueries({ queryKey: [FILES_TREE_KEY] });
            queryClient.invalidateQueries({ queryKey: ['documents'] });
            queryClient.invalidateQueries({
                queryKey: [CORRECTIONS_KEY, docId],
            });
            queryClient.invalidateQueries({
                queryKey: ['leasingDocument', docId],
            });
        },
    });
};

export const useUpdateDocCompletionStatus = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            docId,
            completionStatus,
        }: {
            docId: number;
            completionStatus: 'in_progress' | 'completed';
        }) => {
            await client.patch(`/api/leasing/docs/${docId}/completion_status`, {
                completion_status: completionStatus,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['leasingDocument'],
            });
        },
    });
};
